import { useEffect, useState, useCallback } from "react";
import { toast } from "sonner";
import { Sparkles, Trash2, Pencil, Link2, BarChart3, Mail } from "lucide-react";
import HostEmailDialog from "./HostEmailDialog";
import api, { formatApiError } from "../../lib/api";

const STATUSES = ["new", "contacted", "converted", "declined"];
const badge = { new: "bg-[#E0218A] text-white", contacted: "bg-amber-100 text-amber-800", converted: "bg-[#C6FF33] text-[#121212]", declined: "bg-stone-200 text-stone-600" };

function InquiryCard({ q, onConvert, onStatus, onDelete, onEditEvent, onEmail }) {
  const origin = window.location.origin;
  const copy = (txt, msg) => { navigator.clipboard?.writeText(txt); toast.success(msg); };
  const copyTracker = async () => {
    try {
      const { data } = await api.post(`/admin/classes/${q.class_id}/tracker`);
      copy(`${origin}/rsvp/${data.token}`, "RSVP tracker link copied — send it to the host");
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };
  return (
    <div data-testid={`pinq-${q.id}`} className="p-5 rounded-2xl bg-white border border-stone-200">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <div className="flex items-center gap-2">
            <span className={`px-2 py-0.5 text-[10px] tracking-[0.15em] uppercase font-bold rounded ${badge[q.status] || badge.new}`}>{q.status}</span>
            <span className="text-xs text-stone-400">{(q.created_at || "").slice(0, 10)}</span>
          </div>
          <p className="font-serif font-black text-xl text-[#121212] mt-2">{q.event_type} · {q.num_guests} guests</p>
          <p className="text-sm text-stone-700">{q.name} · <a href={`mailto:${q.email}`} className="underline">{q.email}</a> · <a href={`tel:${q.phone}`} className="underline">{q.phone}</a></p>
          <p className="text-sm text-stone-500 mt-1">Preferred: {q.preferred_date || "—"}{q.alternate_date ? ` (alt ${q.alternate_date})` : ""}{q.preferred_time ? ` · ${q.preferred_time}` : ""}</p>
          {q.notes && <p className="text-sm text-stone-600 mt-2 italic">"{q.notes}"</p>}
        </div>
        <div className="flex items-center gap-2">
          <select data-testid={`pinq-status-${q.id}`} value={q.status} onChange={(e) => onStatus(q, e.target.value)} className="px-3 py-2 rounded-lg border border-stone-200 text-sm bg-white">
            {STATUSES.map((s) => <option key={s} value={s}>{s}</option>)}
          </select>
          <button data-testid={`pinq-delete-${q.id}`} onClick={() => onDelete(q)} className="p-2 rounded-full border border-stone-300 text-red-500"><Trash2 size={15} /></button>
        </div>
      </div>
      <div className="mt-4 flex flex-wrap gap-2">
        {!q.class_id ? (
          <button data-testid={`pinq-convert-${q.id}`} onClick={() => onConvert(q)} className="px-4 py-2 rounded-full bg-[#121212] text-[#C6FF33] text-xs font-bold inline-flex items-center gap-1"><Sparkles size={14} /> Create private event</button>
        ) : (
          <>
            <button data-testid={`pinq-email-host-${q.id}`} onClick={() => onEmail(q.class_id)} className="px-4 py-2 rounded-full bg-[#C6FF33] border border-[#121212] text-xs font-bold inline-flex items-center gap-1"><Mail size={14} /> {q.host_emailed_at ? "Email host again" : "Email host"}</button>
            <button data-testid={`pinq-edit-event-${q.id}`} onClick={() => onEditEvent(q)} className="px-4 py-2 rounded-full bg-[#121212] text-white text-xs font-bold inline-flex items-center gap-1"><Pencil size={14} /> Edit event</button>
            <button data-testid={`pinq-copy-tracker-${q.id}`} onClick={copyTracker} className="px-4 py-2 rounded-full border border-stone-300 text-xs font-bold inline-flex items-center gap-1"><BarChart3 size={14} /> Copy RSVP tracker</button>
            <button data-testid={`pinq-copy-link-${q.id}`} onClick={() => onEditEvent(q, true)} className="px-4 py-2 rounded-full border border-stone-300 text-xs font-bold inline-flex items-center gap-1"><Link2 size={14} /> Copy booking link</button>
          </>
        )}
      </div>
    </div>
  );
}

export default function AdminPrivateInquiries({ openClassEditor, newPrivateEvent }) {
  const [list, setList] = useState([]);
  const [emailFor, setEmailFor] = useState(null);
  const load = useCallback(() => api.get("/admin/private-inquiries").then(({ data }) => setList(data)).catch(() => toast.error("Couldn't load inquiries")), []);
  useEffect(() => { load(); }, [load]);

  const convert = async (q) => {
    try {
      const { data } = await api.post(`/admin/private-inquiries/${q.id}/convert`);
      toast.success("Draft private event created — set the price, then tick Active and save.");
      openClassEditor(data);
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };
  const status = async (q, s) => { await api.patch(`/admin/private-inquiries/${q.id}`, { status: s }); load(); };
  const del = async (q) => { if (!window.confirm(`Delete inquiry from ${q.name}?`)) return; await api.delete(`/admin/private-inquiries/${q.id}`); load(); };
  const editEvent = async (q, copyOnly) => {
    try {
      const { data } = await api.get(`/classes/${q.class_id}`);
      if (copyOnly) { navigator.clipboard?.writeText(`${window.location.origin}/classes/${data.slug}`); toast.success("Private booking link copied"); return; }
      openClassEditor(data);
    } catch { toast.error("That event was deleted."); }
  };

  return (
    <div data-testid="admin-private-inquiries" className="mt-10 space-y-3">
      <button data-testid="new-private-event-btn" onClick={newPrivateEvent} className="w-full p-4 bg-[#121212] text-[#C6FF33] border-2 border-[#121212] text-left font-bold inline-flex items-center gap-2"><Sparkles size={18} /> Create a private event (no request needed)</button>
      <p className="text-sm text-stone-500">Requests from the "Private events" form on your Parties page. "Create private event" makes a hidden, link-only draft with the details filled in.</p>
      {list.map((q) => <InquiryCard key={q.id} q={q} onConvert={convert} onStatus={status} onDelete={del} onEditEvent={editEvent} onEmail={setEmailFor} />)}
      <HostEmailDialog classId={emailFor} open={!!emailFor} onClose={(sent) => { setEmailFor(null); if (sent) load(); }} />
      {!list.length && <p data-testid="pinq-empty" className="text-stone-500">No private event inquiries yet.</p>}
    </div>
  );
}
